import React from 'react'
import PropTypes from 'prop-types'
import {
  SkeletonBase,
  SkeletonText,
  SkeletonTitle,
  SkeletonBadge,
  SkeletonButton,
  SkeletonSelect,
  SkeletonCard
} from './styles'

/**
 * Componente base de Skeleton com animação de pulse
 */
export const Skeleton = ({ width, height, borderRadius, style }) => (
  <SkeletonBase $width={width} $height={height} $borderRadius={borderRadius} style={style} />
)

Skeleton.propTypes = {
  width: PropTypes.string,
  height: PropTypes.string,
  borderRadius: PropTypes.string,
  style: PropTypes.object
}

Skeleton.defaultProps = {
  width: '100%',
  height: '20px',
  borderRadius: '4px',
  style: {}
}

/**
 * Skeleton para texto simples
 */
export const TextSkeleton = ({ width, lines, gap }) => (
  <>
    {Array.from({ length: lines }).map((_, index) => (
      <SkeletonText
        key={index}
        $width={index === lines - 1 && lines > 1 ? '70%' : width}
        style={{ marginBottom: index < lines - 1 ? gap : 0 }}
      />
    ))}
  </>
)

TextSkeleton.propTypes = {
  width: PropTypes.string,
  lines: PropTypes.number,
  gap: PropTypes.string
}

TextSkeleton.defaultProps = {
  width: '100%',
  lines: 1,
  gap: '8px'
}

/**
 * Skeleton para títulos
 */
export const TitleSkeleton = ({ width }) => <SkeletonTitle $width={width} />

TitleSkeleton.propTypes = {
  width: PropTypes.string
}

TitleSkeleton.defaultProps = {
  width: '60%'
}

/**
 * Skeleton para badges
 */
export const BadgeSkeleton = ({ width }) => <SkeletonBadge $width={width} />

BadgeSkeleton.propTypes = {
  width: PropTypes.string
}

BadgeSkeleton.defaultProps = {
  width: '120px'
}

/**
 * Skeleton para botões
 */
export const ButtonSkeleton = ({ width, height }) => <SkeletonButton $width={width} $height={height} />

ButtonSkeleton.propTypes = {
  width: PropTypes.string,
  height: PropTypes.string
}

ButtonSkeleton.defaultProps = {
  width: '100%',
  height: '56px'
}

/**
 * Skeleton para selects/dropdowns
 */
export const SelectSkeleton = ({ width, height }) => <SkeletonSelect $width={width} $height={height} />

SelectSkeleton.propTypes = {
  width: PropTypes.string,
  height: PropTypes.string
}

SelectSkeleton.defaultProps = {
  width: '100%',
  height: '48px'
}

/**
 * Skeleton para cards completos
 */
export const CardSkeleton = ({ children }) => <SkeletonCard>{children}</SkeletonCard>

CardSkeleton.propTypes = {
  children: PropTypes.node
}

/**
 * Skeleton para a seção de Entrada
 */
export const EntrySectionSkeleton = () => (
  <div style={{ marginBottom: '32px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
      <Skeleton width="80px" height="20px" />
      <Skeleton width="140px" height="16px" />
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
      <Skeleton width="160px" height="32px" borderRadius="6px" />
      <BadgeSkeleton width="130px" />
    </div>
    <Skeleton width="200px" height="16px" />
  </div>
)

/**
 * Skeleton para a seção de Parcelas
 */
export const InstallmentsSectionSkeleton = () => (
  <div style={{ marginBottom: '32px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
    <Skeleton width="80px" height="20px" />
    <Skeleton width="100%" height="18px" style={{ maxWidth: '380px' }} />
    <SelectSkeleton />
  </div>
)

/**
 * Skeleton para o conteúdo principal da simulação
 */
export const SimulationContentSkeleton = () => (
  <>
    <EntrySectionSkeleton />
    <div style={{ width: '100%', height: '1px', background: '#e5e7eb', margin: '24px 0' }} />
    <InstallmentsSectionSkeleton />
    <div
      style={{
        background: '#EFF6FF',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px'
      }}
    >
      <Skeleton width="24px" height="24px" borderRadius="50%" />
      <div style={{ flex: 1 }}>
        <Skeleton width="100%" height="16px" style={{ marginBottom: '8px' }} />
        <Skeleton width="80%" height="16px" />
      </div>
    </div>
    <ButtonSkeleton />
  </>
)

/**
 * Skeleton para o SummaryCard
 */
export const SummaryCardSkeleton = () => (
  <div
    style={{
      background: 'linear-gradient(180deg, #1E3A8A 0%, #1E40AF 100%)',
      borderRadius: '12px',
      padding: '24px',
      color: 'white'
    }}
  >
    <Skeleton width="140px" height="20px" style={{ marginBottom: '16px', opacity: 0.3 }} />
    <div
      style={{
        background: 'rgba(255,255,255,0.1)',
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}
    >
      <Skeleton width="32px" height="32px" borderRadius="50%" style={{ opacity: 0.3 }} />
      <Skeleton width="180px" height="16px" style={{ opacity: 0.3 }} />
    </div>

    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <Skeleton width="100px" height="14px" style={{ opacity: 0.3 }} />
        <Skeleton width="80px" height="14px" style={{ opacity: 0.3 }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <Skeleton width="120px" height="20px" style={{ opacity: 0.3 }} />
        <Skeleton width="100px" height="20px" style={{ opacity: 0.3 }} />
      </div>
    </div>

    <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '16px' }}>
      {[1, 2, 3].map(i => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
          <Skeleton width="80px" height="14px" style={{ opacity: 0.3 }} />
          <Skeleton width="100px" height="14px" style={{ opacity: 0.3 }} />
        </div>
      ))}
    </div>

    <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '16px', marginTop: '8px' }}>
      {[1, 2].map(i => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <Skeleton width="100px" height="12px" style={{ opacity: 0.2 }} />
          <Skeleton width="80px" height="12px" style={{ opacity: 0.2 }} />
        </div>
      ))}
    </div>
  </div>
)
